import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login, register } from "@/api/auth";
import { useAuth, clearAppCacheOnLogin } from "@/stores/auth";
import {
  validate,
  loginRules,
  registerRules,
  type ValidationErrors,
} from "@/utils/validation";
import logoSvg from "@/assets/logo.svg";

export default function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { t, i18n } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pendingApproval, setPendingApproval] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Clear stale cache from previous user session on login page mount
  useState(() => { clearAppCacheOnLogin(); });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // Client-side validation
    const rules = isRegister ? registerRules : loginRules;
    const values: Record<string, string> = { username, password, ...(isRegister ? { email } : {}) };
    const errors = validate(values, rules);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      if (isRegister) {
        await register(username, email, password);
        setPendingApproval(true);
        setSubmitting(false);
        return;
      }
      await login(username, password);
      await refresh();
      navigate("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.unknownError");
      if (msg === "pending_approval") {
        setPendingApproval(true);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      {/* Language selector */}
      <div className="login-lang-selector">
        <button
          className={`lang-btn${i18n.language === "fr" ? " lang-active" : ""}`}
          onClick={() => i18n.changeLanguage("fr")}
        >
          {t("language.fr")}
        </button>
        <button
          className={`lang-btn${i18n.language === "en" ? " lang-active" : ""}`}
          onClick={() => i18n.changeLanguage("en")}
        >
          {t("language.en")}
        </button>
      </div>

      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <h1 className="login-title">
          <img src={logoSvg} alt="ComfyViz" className="login-logo" /> ComfyViz
        </h1>
        <p className="login-subtitle">
          {isRegister ? t("auth.createAccount") : t("auth.login")}
        </p>

        {error && <div className="login-error">{error}</div>}

        {pendingApproval && (
          <div className="login-info" style={{
            background: "var(--warning-bg, #854d0e)",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: 8,
            fontSize: 14,
            textAlign: "center",
            marginBottom: 8,
          }}>
            ⏳ {t("auth.accountCreatedPending")}
          </div>
        )}

        <label>
          {t("auth.username")}
          <input
            type="text"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={fieldErrors.username ? "input-error" : ""}
            autoComplete="username"
          />
          <span className="field-error">{fieldErrors.username ?? ""}</span>
        </label>

        {isRegister && (
          <label>
            {t("auth.email")}
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldErrors.email ? "input-error" : ""}
              autoComplete="email"
            />
            <span className="field-error">{fieldErrors.email ?? ""}</span>
          </label>
        )}

        <label>
          {t("auth.password")}
          <input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={fieldErrors.password ? "input-error" : ""}
            autoComplete={isRegister ? "new-password" : "current-password"}
          />
          <span className="field-error">{fieldErrors.password ?? ""}</span>
        </label>

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting
            ? t("common.loading")
            : isRegister
              ? t("auth.registerButton")
              : t("auth.loginButton")}
        </button>

        {!isRegister && (
          <Link to="/forgot-password" className="btn-link" style={{ textAlign: "center", fontSize: 12 }}>
            {t("auth.forgotPassword")}
          </Link>
        )}

        <button
          type="button"
          className="btn-link"
          onClick={() => {
            setIsRegister(!isRegister);
            setError("");
            setFieldErrors({});
          }}
        >
          {isRegister
            ? t("auth.hasAccount")
            : t("auth.noAccount")}
        </button>
      </form>
    </div>
  );
}
